import { computed, effect, inject, Injectable, Signal, signal } from '@angular/core';
import { Router } from '@angular/router';
import { LayerSpecification, Map as MapLibreMap, MapGeoJSONFeature, MapMouseEvent } from 'maplibre-gl';
import { firstValueFrom, take } from 'rxjs';
// @ts-expect-error No type declaration type exists
import { stringify as wktStringify } from 'wkt';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { MapBaseSource } from '@connect-services/client/components/map/map.component';
import {
    BroadcastChannelEvent,
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { SaveEditModeResultEnum } from '@connect-services/client/models/common.model';
import { EditEventAction } from '@connect-services/client/models/edit-event.model';
import { EntityProperties } from '@connect-services/client/models/panel.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import {
    Deposit,
    DepositsService,
    EditAction,
    ExtraObject,
    LayersService,
    Polygon,
    PrecomputeResult,
} from '@connect-services/client/sdk/api';
import { AssertService } from '@connect-services/client/services/assert.service';
import { ContentLayoutService } from '@connect-services/client/services/content-layout.service';
import { EditEntityDetailService } from '@connect-services/client/services/edit-entity-detail.service';
import { EditModeConfirmationService } from '@connect-services/client/services/edit-mode-confirmation.service';
import { EditModeDeleteModalService } from '@connect-services/client/services/edit-mode-delete-modal.service';
import { EditModeLayerPanelService } from '@connect-services/client/services/edit-mode-layer-panel.service';
import { EditModeLeaveService } from '@connect-services/client/services/edit-mode-leave.service';
import { EditModeStateService } from '@connect-services/client/services/edit-mode-state.service';
import { GeohashService } from '@connect-services/client/services/geohash.service';
import { HomeService } from '@connect-services/client/services/home.service';
import { MapGeohashService } from '@connect-services/client/services/map/geohash/map-geohash.service';
import { SelectionStateService } from '@connect-services/client/services/map/selection/selection-state.service';
import { MapBaseService } from '@connect-services/client/services/map-base.service';
import { ActiveGeometryTypeEnum } from '@connect-services/client/services/map-creation-geom.service';
import { MapDrawService } from '@connect-services/client/services/map-draw.service';
import { MapEditGeomService } from '@connect-services/client/services/map-edit-geom.service';
import { MapHighlightEntityService } from '@connect-services/client/services/map-highlight-entity.service';
import { MapModal, MapModalService } from '@connect-services/client/services/map-modal.service';
import { MapGeoJSONFeatureWithLayer, MapPopupService } from '@connect-services/client/services/map-popup.service';
import { MapSourceService } from '@connect-services/client/services/map-source.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import { ProjectStore } from '@connect-services/client/stores/project.store';

export type EditModePermissions = {
    canCreate: boolean;
    canDelete: boolean;
    canUpdate: boolean;
};

export type EditModeCapabilities = EditModePermissions & {
    enabled: boolean;
};

export interface LayerEditContext {
    isAddEntityModeActive: boolean;
    isGeographicEditModeActive: boolean;
    permissions: EditModePermissions;
    showAddEntity: boolean;
    showGeographicEdit: boolean;
}

export type LayerEditUiConfig = {
    addEntityTooltipKey?: string | null;
    canAddEntity: boolean;
    canGeographicEdit: boolean;
    geographicEditTooltipKey?: string | null;
    isAddEntityModeActive: boolean;
    isGeographicEditModeActive: boolean;
    showAddEntity: boolean;
    showGeographicEdit: boolean;
};

export const entityDetailPanelRegexp = new RegExp(
    `\\/${RoutePathEnum.ENTITY_DETAIL}\\/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}`,
    'i',
);

export const TARGET_CURSOR = 'url("./assets/images/map/target-cursor.png") 8.5 8.5, grab';

export enum EditModeType {
    ADD = 'add',
    GEOGRAPHIC = 'geographic',
    NONE = 'none',
}

export type DeletionCandidate = {
    depositId: string;
    entityId: string;
    layerId: string;
    properties: Record<string, unknown>;
    tableName: string;
};

export type IconSet = {
    icon: string;
    iconSize: number;
};

@Injectable({
    providedIn: 'root',
})
export class MapEditModeService extends MapBaseService {
    public static CREATE_GEOMETRY_LAYER: string = 'creation-mode-layer';
    public static CREATE_GEOMETRY_SOURCE: string = 'creation-mode-source';
    public static EDIT_ADD_VERTEX_LAYER: string = 'edit-mode-add-vertex-layer';
    public static EDIT_ADD_VERTEX_SOURCE: string = 'edit-mode-add-vertex';
    public static EDIT_LAYER: string = 'edit-mode-vertices-layer';
    public static EDIT_SNAP_LAYER: string = 'edit-snap-layer';
    public static EDIT_SNAP_SOURCE: string = 'edit-snap-source';
    public static EDIT_SOURCE: string = 'edit-mode-vertices';
    public static LAYER_SPECIFICATION_ADD_LINE_VERTEX: LayerSpecification = {
        id: MapEditModeService.EDIT_ADD_VERTEX_LAYER,
        layout: {
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'icon-image': 'edit-plus-icon',
            'icon-offset': [20, 20],
            'icon-size': 0.6,
        },
        source: MapEditModeService.EDIT_ADD_VERTEX_SOURCE,
        type: 'symbol',
    };
    public static LAYER_SPECIFICATION_ADD_POLYGON_VERTEX: LayerSpecification = {
        id: MapEditModeService.EDIT_ADD_VERTEX_LAYER,
        layout: {
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'icon-image': 'edit-plus-icon',
            'icon-size': 0.6,
            'symbol-sort-key': 1,
        },
        source: MapEditModeService.EDIT_ADD_VERTEX_SOURCE,
        type: 'symbol',
    };
    public static LAYER_SPECIFICATION_CREATION_GEOMETRY: LayerSpecification = {
        id: MapEditModeService.CREATE_GEOMETRY_LAYER,
        layout: {},
        paint: {
            'line-color': getComputedStyle(document.documentElement).getPropertyValue('--custom-blue').trim(),
            'line-dasharray': [2, 2],
            'line-width': 2,
        },
        source: MapEditModeService.CREATE_GEOMETRY_SOURCE,
        type: 'line',
    };
    public static LAYER_SPECIFICATION_SNAP: LayerSpecification = {
        id: MapEditModeService.EDIT_SNAP_LAYER,
        layout: {
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'icon-image': 'diamond',
            'icon-size': 0.6,
        },
        source: MapEditModeService.EDIT_SNAP_SOURCE,
        type: 'symbol',
    };
    public static MAP_LAYER: LayerSpecification = {
        id: MapEditModeService.EDIT_LAYER,
        layout: {
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'icon-image': 'circle-svg',
            'icon-size': 0.3,
        },
        paint: {},
        source: MapEditModeService.EDIT_SOURCE,
        type: 'symbol',
    };
    public static PREVIEW_GEOMETRY_LAYER: string = 'preview-mode-layer';
    public static PREVIEW_GEOMETRY_SOURCE: string = 'preview-mode-source';

    public readonly activeEditLayerIds = signal<Set<string>>(new Set());
    public readonly activeEditSelectionGeometryType = signal<ActiveGeometryTypeEnum | null>(null);
    public readonly activeGeometryType = signal<ActiveGeometryTypeEnum | null>(null);
    public readonly canRequestDelete = computed(() => {
        return !(!this.isMapEditModeActive() || !this.editPermissions().canDelete);
    });
    public readonly editEntityId = signal<string | undefined>(undefined);
    public readonly editPermissions = signal<EditModePermissions>({
        canCreate: false,
        canDelete: false,
        canUpdate: false,
    });
    public readonly exitEditModeSignal = signal<number>(0);
    public readonly hasPendingEdits = computed(() => !!this.editModeStateService.pendingEditCount());
    public readonly isAddEntityModeActive = computed(() => this.editModeType() === EditModeType.ADD);
    public readonly isAddLineVertexActive = signal<boolean>(false);
    public readonly isAddVertexActive = signal<boolean>(false);
    public readonly isEditEntityActive = computed<boolean>(
        () =>
            this.isAddLineVertexActive() ||
            this.isAddVertexActive() ||
            this.isEditingGeometryActive() ||
            this.isEditVertexActive(),
    );
    public readonly isEditingGeometryActive = signal<boolean>(false);
    public readonly isEditVertexActive = signal<boolean>(false);
    public readonly isGeographicEditModeActive = computed(() => this.editModeType() === EditModeType.GEOGRAPHIC);
    public readonly isMapSelectionControlDisabled = computed<boolean>(() => {
        return (
            this.activeEditSelectionGeometryType() === ActiveGeometryTypeEnum.SYMBOL ||
            this.isGeographicEditModeActive()
        );
    });
    public readonly isSynopticPlanEditModeActive = signal<boolean>(false);
    public readonly selectedSnapSources = signal<string[]>([]);

    private readonly _isMapEditModeActive = signal<boolean>(false);
    private broadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.LAYERS);
    private drawEditedFunctionInstance?: (mouseEvent: MouseEvent) => void;
    private editModeChangeListeners: Array<(active: boolean) => void> = [];
    private readonly editModeType = signal<EditModeType>(EditModeType.NONE);
    private editModeUiSyncInProgress = false;
    private highlightEditGeomVertexFunctionInstance?: (mapMouseEvent: MapMouseEvent) => void;
    private readonly isMapReady = signal(false);
    private lastHandledGlobalEditMode: boolean | null = null;
    private lastNotifiedEditModeActive: boolean | null = null;
    private synopticBroadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.SYNOPTIC);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly contentLayoutService = inject(ContentLayoutService);
    private readonly depositsService = inject(DepositsService);
    private readonly editEntityDetailService = inject(EditEntityDetailService);
    private readonly editModeConfirmationService = inject(EditModeConfirmationService);
    private readonly editModeDeleteModalService = inject(EditModeDeleteModalService);
    private readonly editModeLayerPanelService = inject(EditModeLayerPanelService);
    private readonly editModeLeaveService = inject(EditModeLeaveService);
    private readonly editModeStateService = inject(EditModeStateService);
    private readonly geohashService = inject(GeohashService);
    private readonly homeService = inject(HomeService);
    private readonly layersService = inject(LayersService);
    private readonly mapDrawService = inject(MapDrawService);
    private readonly mapGeohashService = inject(MapGeohashService);
    private readonly mapHighlightEntityService = inject(MapHighlightEntityService);
    private readonly mapModalService = inject(MapModalService);
    private readonly mapPopupService = inject(MapPopupService);
    private readonly mapSourceService = inject(MapSourceService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly projectEditModeService = inject(ProjectEditModeService);
    private readonly projectStore = inject(ProjectStore);
    private readonly router = inject(Router);
    private readonly selectionStateService = inject(SelectionStateService);
    private readonly sourcesService = inject(SourcesService);

    public constructor() {
        super();

        this.listenSynopticEditModeMessages();

        this.broadcastChannel.onmessage = async (
            event: MessageEvent<BroadcastChannelEvent<DeletionCandidate[]>>,
        ): Promise<void> => {
            if (
                event.data.source !== BroadcastChannelSourceEnum.ENTITY_DETAIL ||
                event.data.message !== BroadcastChannelMessageEnum.REDRAW_SOURCE_AFTER_DELETED ||
                !event.data.additionalData
            ) {
                return;
            }

            await this.redrawSourcesAfterDelete(event.data.additionalData);
        };

        effect(async () => {
            const globalActive = this.projectEditModeService.isEditModeActive();

            this._isMapEditModeActive.set(globalActive);

            if (this.lastHandledGlobalEditMode === globalActive) {
                return;
            }

            this.lastHandledGlobalEditMode = globalActive;

            await this.syncMapEditModeUi(globalActive);
        });

        effect(() => {
            if (this._isMapEditModeActive()) {
                this.mapModalService.open(MapModal.EDITION);

                return;
            }

            this.mapModalService.close(MapModal.EDITION);
            this.stopHighlightGeomVertexEvent();
        });

        effect(() => {
            this.selectionStateService.setEditModeActive(this.isMapEditModeActive());
        });
    }

    public get isMapEditModeActive(): Signal<boolean> {
        return this._isMapEditModeActive.asReadonly();
    }

    public async applyMapUiForEditMode(): Promise<void> {
        if (!this.isMapReady() || !this.isMapPresent()) {
            return;
        }

        await this.enterEditMode();
    }

    public async bindMap(map: MapLibreMap): Promise<void> {
        this.map = map;
        this.isMapReady.set(true);

        if (!this.projectEditModeService.isEditModeActive()) {
            return;
        }

        await this.applyMapUiForEditModeOnBind();
    }

    public async cleanupMapUiForEditMode(): Promise<void> {
        this.editModeStateService.clearEditModeState(this.projectBloc.value()!.id);
        this._isMapEditModeActive.set(false);

        if (!this.isMapPresent()) {
            return;
        }

        this.clearLayerEditMode();
        this.exitEditModeSignal.update((value) => value + 1);
        this.editEntityDetailService.isEditMode.set(false);
        await this.editModeLayerPanelService.handleEditModePanelChange(false);
        this.closeModals([MapModal.SNAP]);

        this.notifyEditModeChange();
    }

    public clearLayerEditMode(): void {
        this.editModeType.set(EditModeType.NONE);
        this.activeEditLayerIds.set(new Set());

        if (!this.isMapPresent()) {
            return;
        }

        const layerToClear = [
            MapEditModeService.EDIT_LAYER,
            MapEditModeService.EDIT_ADD_VERTEX_LAYER,
            MapEditModeService.EDIT_SNAP_LAYER,
            MapEditModeService.CREATE_GEOMETRY_LAYER,
            MapEditGeomService.MAP_EDIT_PHANTOM_LAYER_ID,
        ];

        layerToClear
            .filter((layerName) => this.map.getLayer(layerName))
            .forEach((layerName) => this.map.removeLayer(layerName));

        const sourceToClear = [
            MapEditModeService.EDIT_SOURCE,
            MapEditModeService.EDIT_ADD_VERTEX_SOURCE,
            MapEditModeService.EDIT_SNAP_SOURCE,
            MapEditModeService.CREATE_GEOMETRY_SOURCE,
        ];

        sourceToClear
            .filter((sourceName) => this.map.getSource(sourceName))
            .forEach((sourceName) => this.map.removeSource(sourceName));
    }

    public async forceExitEditModeUi(): Promise<void> {
        if (!this._isMapEditModeActive() && !this.isMapPresent()) {
            return;
        }

        this.clearEntityDetailState();

        await this.exitEditMode();
    }

    public getLayerEditConfig(options: {
        showAddEntity: boolean;
        showGeographicEdit: boolean;
    }): LayerEditUiConfig | null {
        if (!this.isMapEditModeActive()) {
            return null;
        }

        const context: LayerEditContext = {
            isAddEntityModeActive: this.isAddEntityModeActive(),
            isGeographicEditModeActive: this.isGeographicEditModeActive(),
            permissions: this.editPermissions(),
            showAddEntity: options.showAddEntity,
            showGeographicEdit: options.showGeographicEdit,
        };

        return this.buildLayerEditConfig(context);
    }

    public isReClickOnSameLayer(sourceIds: string[], mode: EditModeType): boolean {
        const isSameMode = this.editModeType() === mode;
        const isSameLayer = sourceIds.some((id) => this.activeEditLayerIds().has(id));

        return isSameMode && isSameLayer;
    }

    public openEditModeConfirmationModal(): void {
        const hasPendingDeleteEdits = this.editModeStateService.hasPendingDeleteEdits();

        const dialogRef = this.editModeConfirmationService.openSaveChangesModal({
            canEndEditSession: true,
            onSaved: async () => {
                if (!hasPendingDeleteEdits) {
                    return;
                }

                await this.projectStore.refresh();
            },
        });

        dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe(async (result: SaveEditModeResultEnum | undefined) => {
                if (result !== SaveEditModeResultEnum.SAVED_QUIT) {
                    return;
                }

                await this.projectStore.refresh();
                this.projectEditModeService.deactivateEditMode();
                this.clearEntityDetailState();
            });
    }

    public registerEditModeChangeListener(listener: (active: boolean) => void): void {
        this.editModeChangeListeners.push(listener);
    }

    public async requestDelete(
        feature: MapGeoJSONFeatureWithLayer | EntityProperties | null,
        isFromTableView: boolean = false,
    ): Promise<boolean> {
        if (!this.canRequestDelete()) {
            return Promise.resolve(false);
        }

        const deletionCandidates = await this.resolveDeletionCandidates(feature);

        if (!deletionCandidates.length) {
            return Promise.resolve(false);
        }

        return this.openDeleteConfirmationModal(
            {
                count: deletionCandidates.length,
                entities: deletionCandidates,
            },
            isFromTableView,
        );
    }

    public async requestToggleEditMode(): Promise<void> {
        const globalActive = this.projectEditModeService.isEditModeActive();

        if (globalActive && this.hasPendingEdits()) {
            const result = await this.editModeLeaveService.requestLeaveEditMode();
            if (!result.canLeave) {
                return;
            }

            this.projectEditModeService.deactivateEditMode();

            return;
        }

        if (globalActive) {
            this.projectEditModeService.deactivateEditMode();

            return;
        }

        this.projectEditModeService.activateEditMode();
    }

    public stopLayerEditMode(): void {
        this.activeGeometryType.set(null);
        this.activeEditSelectionGeometryType.set(null);
        this.applyNoneMode();
    }

    public toggleLayerEditMode(sourceIds: string[], mode: EditModeType, entityId?: string): void {
        if (!this.isMapEditModeActive() || !this.isMapPresent()) {
            return;
        }

        if (this.isReClickOnSameLayer(sourceIds, mode)) {
            this.resetAfterReClick(sourceIds);

            return;
        }

        if (this.isNoneMode(mode)) {
            this.applyNoneMode();

            return;
        }

        const newActiveEditLayersIds = this.computeNextActiveLayers(sourceIds, mode);

        this.activeEditLayerIds.set(new Set([...newActiveEditLayersIds]));
        this.editModeType.set(mode);

        if (this.shouldExitEditMode(newActiveEditLayersIds, mode)) {
            this.resetLayerEditState();

            return;
        }

        if (mode === EditModeType.ADD) {
            this.activateAddMode(sourceIds);

            return;
        }

        this.handleSwitchFromAddMode(mode);

        if (mode === EditModeType.GEOGRAPHIC) {
            this.mapModalService.open(MapModal.SNAP);
        }

        this.activateGeographicMode(entityId);
    }

    public updateEditPermissions(permissions: EditModePermissions): void {
        this.editPermissions.set(permissions);
    }

    private activateAddMode(sourceIds: string[]): void {
        this.clearLayerEditMode();
        this.editModeType.set(EditModeType.ADD);
        this.activeEditLayerIds.set(new Set(sourceIds));
        this.mapModalService.open(MapModal.SNAP);

        if (!this.isMapPresent()) {
            return;
        }

        this.ensureDrawEditedFunction();
        this.drawEditSourceOnMove();

        this.map.on('moveend', this.drawEditedFunctionInstance!);
    }

    private activateGeographicMode(entityId?: string): void {
        if (!this.map) {
            return;
        }

        this.ensureDrawEditedFunction();

        this.editEntityId.set(entityId);
        this.drawEditSourceOnMove();

        this.initHighlightGeomVertexEvent();

        this.map.on('moveend', this.drawEditedFunctionInstance!);
    }

    private async applyMapUiForEditModeOnBind(): Promise<void> {
        if (!this.isMapPresent()) {
            return;
        }

        await this.enterEditMode();
    }

    private applyNoneMode(): void {
        this.activeEditLayerIds.set(new Set());
        this.clearLayerEditMode();

        if (this.drawEditedFunctionInstance && this.map) {
            this.map.off('moveend', this.drawEditedFunctionInstance);
        }

        this.closeModals([MapModal.SNAP]);
        this.editModeType.set(EditModeType.NONE);
    }

    private buildAddEntityTooltip(context: LayerEditContext, canAddEntity: boolean): string | null {
        return this.buildToggleTooltip(
            context.showAddEntity,
            canAddEntity,
            context.isAddEntityModeActive,
            'noCreatePermission',
            'addEntityMode',
            'closeAddEntityMode',
        );
    }

    private async buildDeletionCandidateFromFeature(
        feature: MapGeoJSONFeatureWithLayer | EntityProperties | null,
    ): Promise<DeletionCandidate | null> {
        if (!feature) {
            return null;
        }
        const properties: Record<string, unknown> = feature.properties ?? feature;

        AssertService.hasKey(properties, 'uuid');
        AssertService.isString(properties['uuid']);
        AssertService.hasKey(properties, 'deposit_id');
        AssertService.isString(properties['deposit_id']);

        let layerId: string;
        if ('source' in feature) {
            layerId = feature.source;
        } else {
            AssertService.hasKey(properties, 'layerId');
            AssertService.isString(properties['layerId']);
            layerId = properties['layerId'];
        }
        const tableName = await this.getLayerTableName(layerId);

        return {
            depositId: properties['deposit_id'],
            entityId: properties['uuid'],
            layerId,
            properties: properties,
            tableName: tableName,
        };
    }

    private async buildDeletionCandidatesFromSelectionState(): Promise<DeletionCandidate[]> {
        const entitiesByLayerId: Record<string, Record<string, unknown>[]> =
            this.selectionStateService.selectedEntitiesByLayerId();

        if (!Object.keys(entitiesByLayerId).length) {
            return Promise.resolve([]);
        }

        const promises = Object.entries(entitiesByLayerId).map(
            async ([layerId, properties]: [string, Record<string, unknown>[]]) => {
                const tableName = await this.getLayerTableName(layerId);

                return properties.map((entityProperties: Record<string, unknown>) => {
                    AssertService.isString(entityProperties['uuid']);
                    AssertService.isString(entityProperties['deposit_id']);

                    return {
                        depositId: entityProperties['deposit_id'],
                        entityId: entityProperties['uuid'],
                        layerId,
                        properties: entityProperties,
                        tableName: tableName,
                    };
                });
            },
        );

        return (await Promise.all(promises)).flat();
    }

    private buildGeographicEditTooltip(context: LayerEditContext, canGeographicEdit: boolean): string | null {
        return this.buildToggleTooltip(
            context.showGeographicEdit,
            canGeographicEdit,
            context.isGeographicEditModeActive,
            'noCreatePermission',
            'editMode.geographic',
            'editMode.closeGeographic',
        );
    }

    private buildLayerEditConfig(context: LayerEditContext): LayerEditUiConfig {
        const canAddEntity: boolean = context.permissions.canCreate && context.showAddEntity;
        const canGeographicEdit: boolean = context.permissions.canUpdate && context.showGeographicEdit;

        return {
            addEntityTooltipKey: this.buildAddEntityTooltip(context, canAddEntity),
            canAddEntity,
            canGeographicEdit,
            geographicEditTooltipKey: this.buildGeographicEditTooltip(context, canGeographicEdit),
            isAddEntityModeActive: context.isAddEntityModeActive,
            isGeographicEditModeActive: context.isGeographicEditModeActive,
            showAddEntity: context.showAddEntity,
            showGeographicEdit: context.showGeographicEdit,
        };
    }

    private buildPolygonFromMapBbox(): Polygon {
        const bounds = this.map!.getBounds();

        const west = bounds.getWest();
        const east = bounds.getEast();
        const south = bounds.getSouth();
        const north = bounds.getNorth();

        return {
            coordinates: [
                [
                    [west, south],
                    [east, south],
                    [east, north],
                    [west, north],
                    [west, south],
                ],
            ],
            type: Polygon.TypeEnum.POLYGON,
        };
    }

    private buildToggleTooltip(
        show: boolean,
        havePermission: boolean,
        isActive: boolean,
        noPermissionKey: string,
        inactiveKey: string,
        activeKey: string,
    ): string | null {
        if (!show) {
            return null;
        }

        if (this.contentLayoutService.isDataTableView()) {
            return 'visibleMapRequired';
        }

        if (!havePermission) {
            return noPermissionKey;
        }

        return isActive ? activeKey : inactiveKey;
    }

    private clearEntityDetailState(): void {
        this.editEntityDetailService.isEditMode.set(false);
        this.editEntityDetailService.entityId.set('');
    }

    private closeModals(modals: MapModal[]): void {
        modals.forEach((modal) => this.mapModalService.close(modal));
    }

    private computeNextActiveLayers(sourceIds: string[], mode: EditModeType): Set<string> {
        if (mode !== this.editModeType()) {
            return new Set(sourceIds);
        }

        const isAtLeastOneEditMode = sourceIds.some((sourceId) => this.activeEditLayerIds().has(sourceId));

        const newActiveEditLayersIds = new Set([...this.activeEditLayerIds()]);

        sourceIds.forEach((sourceId) => {
            if (isAtLeastOneEditMode) {
                newActiveEditLayersIds.delete(sourceId);

                return;
            }

            newActiveEditLayersIds.add(sourceId);
        });

        return newActiveEditLayersIds;
    }

    private async drawEditSourceOnMove(): Promise<void> {
        if (!this.isEditEntityActive() && !this.isAddEntityModeActive()) {
            const bboxPolygon = this.buildPolygonFromMapBbox();

            await this.mapSourceService.disableSourcesZoom(
                [...this.activeEditLayerIds()],
                true,
                wktStringify(bboxPolygon),
            );
            await this.mapSourceService.applyEditStyle([...this.activeEditLayerIds()], this.editEntityId());
        }

        if (!this.selectedSnapSources().length || (!this.isEditEntityActive() && !this.isAddEntityModeActive())) {
            return;
        }

        await this.mapSourceService.applySnapStyle(this.selectedSnapSources());
    }

    private ensureDrawEditedFunction(): void {
        if (this.drawEditedFunctionInstance) {
            return;
        }

        this.drawEditedFunctionInstance = this.drawEditSourceOnMove.bind(this);
    }

    private async enterEditMode(): Promise<void> {
        if (!this.isMapPresent()) {
            return;
        }

        if (!this._isMapEditModeActive()) {
            this._isMapEditModeActive.set(true);
        }

        if (
            !this.projectEditModeService.consumePreserveEntityDetailPanel() &&
            !entityDetailPanelRegexp.test(this.router.url)
        ) {
            this.clearEntityDetailState();
            await this.editModeLayerPanelService.handleEditModePanelChange(true);
        }

        this.notifyEditModeChange();
    }

    private async exitEditMode(): Promise<void> {
        this._isMapEditModeActive.set(false);

        if (!this.isMapPresent()) {
            return;
        }

        this.clearEntityDetailState();
        this.clearLayerEditMode();
        this.exitEditModeSignal.update((value) => value + 1);
        await this.editModeLayerPanelService.handleEditModePanelChange(false);
        this.notifyEditModeChange();
    }

    private getDeletedEntitiesSources(entities: DeletionCandidate[]): MapBaseSource<unknown>[] | undefined {
        if (!entities.length) {
            return;
        }

        const sourceIds = [...new Set(entities.map((entity) => entity.layerId))];
        if (!sourceIds.length) {
            return;
        }

        return this.sourcesService.getSourcesByIds(sourceIds);
    }

    private async getLayerTableName(layerId: string): Promise<string> {
        try {
            const layer = await firstValueFrom(this.layersService.layerControllerGetOne({ id: layerId }));
            AssertService.isString(layer.tableName);

            return layer.tableName;
        } catch {
            return '';
        }
    }

    private handleHoveredVertexHighlight(mapMouseEvent: MapMouseEvent): void {
        if (this.isEditEntityActive()) {
            return;
        }

        let hoverEditVertexFeatures: MapGeoJSONFeature[] | undefined = [];
        if (this.map?.getLayer(MapEditModeService.EDIT_LAYER)) {
            hoverEditVertexFeatures = this.map?.queryRenderedFeatures(mapMouseEvent.point, {
                layers: [MapEditModeService.EDIT_LAYER],
            });
        }

        if (hoverEditVertexFeatures?.length) {
            this.highlightEditVertex(hoverEditVertexFeatures.map((feature) => feature.properties['uniqueId']));

            return;
        }

        if (!this.map?.getLayer(MapEditModeService.EDIT_ADD_VERTEX_LAYER)) {
            return;
        }

        const hoverAddVertexFeatures = this.map?.queryRenderedFeatures(mapMouseEvent.point, {
            layers: [MapEditModeService.EDIT_ADD_VERTEX_LAYER],
        });

        this.highlightAddVertex((hoverAddVertexFeatures ?? []).map((feature) => feature.properties['uniqueId']));
    }

    private handleSwitchFromAddMode(mode: EditModeType): void {
        if (this.editModeType() !== EditModeType.ADD || this.editModeType() === mode) {
            return;
        }

        this.clearLayerEditMode();
    }

    private highlightAddVertex(entitiesId: string[]): void {
        this.resetHoveredVertexHighlight(MapEditModeService.EDIT_LAYER, {
            icon: 'circle-svg',
            iconSize: 0.3,
        });
        if (!entitiesId.length) {
            this.resetHoveredVertexHighlight(MapEditModeService.EDIT_ADD_VERTEX_LAYER, {
                icon: 'edit-plus-icon',
                iconSize: 0.6,
            });

            return;
        }

        this.highlightHoveredVertex(
            MapEditModeService.EDIT_ADD_VERTEX_LAYER,
            entitiesId,
            {
                icon: 'edit-plus-icon',
                iconSize: 0.6,
            },
            { icon: 'edit-plus-outline-icon', iconSize: 1 },
        );
    }

    private highlightEditVertex(entitiesId: string[]): void {
        this.resetHoveredVertexHighlight(MapEditModeService.EDIT_ADD_VERTEX_LAYER, {
            icon: 'edit-plus-icon',
            iconSize: 0.6,
        });
        this.highlightHoveredVertex(
            MapEditModeService.EDIT_LAYER,
            entitiesId,
            {
                icon: 'circle-svg',
                iconSize: 0.3,
            },
            { icon: 'circle-outline-svg', iconSize: 0.8 },
        );
    }

    private highlightHoveredVertex(
        layerId: string,
        uniqueIds: string[],
        defaultIconSet: IconSet,
        highlightIconSet: IconSet,
    ): void {
        this.mapHighlightEntityService.updateLayoutProperty(
            layerId,
            ['icon-size', 'icon-image'],
            uniqueIds,
            'uniqueId',
            [highlightIconSet.iconSize, highlightIconSet.icon],
            [defaultIconSet.iconSize, defaultIconSet.icon],
        );
    }

    private initHighlightGeomVertexEvent(): void {
        if (!this.map) {
            return;
        }

        if (!this.highlightEditGeomVertexFunctionInstance) {
            this.highlightEditGeomVertexFunctionInstance = this.handleHoveredVertexHighlight.bind(this);
        }

        this.map.on('mousemove', this.highlightEditGeomVertexFunctionInstance);
    }

    private isNoneMode(mode: EditModeType): boolean {
        return mode === EditModeType.NONE;
    }

    private listenSynopticEditModeMessages(): void {
        this.synopticBroadcastChannel.onmessage = (event: MessageEvent<BroadcastChannelEvent<boolean>>): void => {
            const data = event.data;

            if (
                data.source !== BroadcastChannelSourceEnum.SYNOPTIC_PLAN ||
                data.message !== BroadcastChannelMessageEnum.SYNOPTIC_EDIT_MODE_ACTIVE
            ) {
                return;
            }

            this.isSynopticPlanEditModeActive.set(!!data.additionalData);
        };
    }

    private notifyEditModeChange(): void {
        if (this.lastNotifiedEditModeActive === this._isMapEditModeActive()) {
            return;
        }

        this.lastNotifiedEditModeActive = this._isMapEditModeActive();

        for (const listener of this.editModeChangeListeners) {
            listener(this._isMapEditModeActive());
        }
    }

    private async openDeleteConfirmationModal(
        payload: {
            count: number;
            entities: DeletionCandidate[];
        },
        isFromTableView: boolean = false,
    ): Promise<boolean> {
        const projectId = this.projectBloc.value()?.id;
        const editSessionId = this.projectEditModeService.getEditSession()?.id;

        if (!payload.count || !projectId || !editSessionId) {
            return false;
        }

        const confirmed = await this.editModeDeleteModalService.openDeleteEntitiesDialog(payload.count);
        if (!confirmed) {
            return false;
        }

        const hasSaveActions = await this.saveDeleteActions(payload.entities, projectId, editSessionId);
        if (!hasSaveActions) {
            return false;
        }

        if (isFromTableView) {
            return true;
        }

        this.mapPopupService.closePopup();
        this.mapDrawService.clearDrawnPolygons();
        this.selectionStateService.clearEntitiesAndLayer();

        if (this.homeService.isExternalPage()) {
            this.broadcastChannel.postMessage(<BroadcastChannelEvent<DeletionCandidate[]>>{
                additionalData: payload.entities,
                message: BroadcastChannelMessageEnum.REDRAW_SOURCE_AFTER_DELETED,
                source: BroadcastChannelSourceEnum.ENTITY_DETAIL,
            });

            return true;
        }

        await this.redrawSourcesAfterDelete(payload.entities);

        return true;
    }

    private async redrawSourcesAfterDelete(entities: DeletionCandidate[]): Promise<void> {
        const sources = this.getDeletedEntitiesSources(entities);
        if (!sources?.length) {
            return;
        }

        let geoHashes;

        if (this.mapGeohashService.centralGeohash && MapGeohashService.geohashNeighborsMatrixSize) {
            geoHashes = [
                ...this.geohashService.findNeighbors(
                    this.mapGeohashService.centralGeohash!,
                    MapGeohashService.geohashNeighborsMatrixSize!,
                ),
                this.mapGeohashService.centralGeohash!,
            ];
        }

        await this.mapSourceService.drawSources(
            sources,
            {
                fitBounds: false,
                geohashes: geoHashes,
            },
            true,
        );
    }

    private resetAfterReClick(sourcesIds: string[]): void {
        this.activeEditLayerIds.update(
            (activeEditLayerIds) =>
                new Set(
                    Array.from(activeEditLayerIds).filter(
                        (activeEditLayerId) => !sourcesIds.includes(activeEditLayerId),
                    ),
                ),
        );

        if (!this.map) {
            return;
        }

        this.drawEditSourceOnMove();

        if (this.activeEditLayerIds().size) {
            return;
        }

        this.clearLayerEditMode();

        this.closeModals([MapModal.SNAP]);

        if (this.drawEditedFunctionInstance) {
            this.map.off('moveend', this.drawEditedFunctionInstance);
        }

        this.stopHighlightGeomVertexEvent();

        this.map.getCanvas().style.cursor = '';
        this.editModeType.set(EditModeType.NONE);
    }

    private resetHoveredVertexHighlight(layerId: string, iconSet: IconSet): void {
        this.mapHighlightEntityService.resetLayoutAndPaintProperties(
            layerId,
            ['icon-size', 'icon-image'],
            [iconSet.iconSize, iconSet.icon],
        );
    }

    private resetLayerEditState(): void {
        if (!this.map) {
            return;
        }

        this.clearLayerEditMode();
        this.closeModals([MapModal.SNAP]);

        this.stopHighlightGeomVertexEvent();

        if (!this.drawEditedFunctionInstance) {
            return;
        }

        this.map.off('moveend', this.drawEditedFunctionInstance);
    }

    private async resolveDeletionCandidates(
        entity: MapGeoJSONFeatureWithLayer | EntityProperties | null,
    ): Promise<DeletionCandidate[]> {
        const selectionCandidates = await this.buildDeletionCandidatesFromSelectionState();

        if (selectionCandidates.length) {
            return selectionCandidates;
        }

        const candidate = await this.buildDeletionCandidateFromFeature(entity);

        return candidate ? [candidate] : [];
    }

    private async saveDeleteActions(
        entities: DeletionCandidate[],
        projectId: string,
        editSessionId: string,
    ): Promise<boolean> {
        const editActions: EditEventAction[] = this.editModeStateService.getBulkDelete({
            candidates: entities,
            projectId,
        });

        const extraObjects: ExtraObject[] = [];
        const deposits: Map<string, Deposit> = new Map();

        for (const entity of entities) {
            try {
                let deposit: Deposit | undefined = deposits.get(entity.depositId);

                if (!deposit) {
                    const newDeposit = await firstValueFrom(
                        this.depositsService.depositControllerGetOne({
                            id: entity.depositId,
                        }),
                    );

                    deposits.set(entity.depositId, newDeposit);
                    deposit = newDeposit;
                }

                if (!deposit.standard) {
                    continue;
                }

                const recomputeResult: PrecomputeResult = await firstValueFrom(
                    this.layersService.layerControllerPostEntityPrecompute({
                        entityPrecomputeDto: {
                            action: EditAction.DELETE,
                            attributes: entity.properties,
                            editSessionId,
                            entityId: entity.entityId,
                            sourceAttributes: null,
                            sourceTableName: undefined,
                        },
                        layerId: entity.layerId,
                    }),
                );

                if (!recomputeResult?.success) {
                    this.editEntityDetailService.recomputeError.set(recomputeResult?.message ?? null);

                    return false;
                }

                const editAction: EditEventAction | undefined = editActions.find(
                    (action: EditEventAction) =>
                        action.entityId === entity.entityId &&
                        action.layerId === entity.layerId &&
                        action.tableName === entity.tableName,
                );

                if (!editAction) {
                    return false;
                }

                editAction.saveOrder = recomputeResult.saveOrder;

                extraObjects.push(...recomputeResult.extraObjects);
            } catch {
                return false;
            }
        }

        this.editEntityDetailService.saveExtraObjects(extraObjects, editActions, projectId);

        return true;
    }

    private shouldExitEditMode(newActiveEditLayersIds: Set<string>, mode: EditModeType): boolean {
        return !newActiveEditLayersIds.size && this.editModeType() === mode;
    }

    private stopHighlightGeomVertexEvent(): void {
        if (!this.map || !this.highlightEditGeomVertexFunctionInstance) {
            return;
        }

        this.map.off('mousemove', this.highlightEditGeomVertexFunctionInstance);
    }

    private async syncMapEditModeUi(globalActive: boolean): Promise<void> {
        if (this.editModeUiSyncInProgress) {
            return;
        }

        this.editModeUiSyncInProgress = true;

        try {
            if (!this.isMapReady() || !this.isMapPresent()) {
                return;
            }

            if (globalActive) {
                await this.applyMapUiForEditMode();

                return;
            }

            await this.cleanupMapUiForEditMode();
        } finally {
            this.editModeUiSyncInProgress = false;
        }
    }
}
